import { useState, useEffect, useCallback, useMemo } from "react";
import type { BusinessInvitation, BusinessSubscription, LensSDKException, UserBusinessRole } from "lens-platform-sdk";
import { Modal } from "@k8slens/lds";
import { FormField } from "@k8slens/lds-form";
import { useHistory } from "react-router-dom";

import { useAnalytics } from "src/hooks/useAnalytics";
import { getUniqueEmailsArray, validateEmail } from "src/services/email";
import { getSubscriptionSeatCount } from "src/services/getSubscriptionSeatCount";
import { getPlanName } from "src/services/plans";

import Notification from "src/components/Notification/Notification";
import TrackedButton from "src/components/Button/TrackedButton";
import { FormSelect } from "src/components/FormComponents/FormSelect";

import SubscriptionSelectContent from "./SubscriptionSelectContent";

import styles from "./AddUsersModal.module.css";

interface Props {
  open: boolean;
  adding: boolean;
  errorAdding?: LensSDKException | null;
  loadingBusinessSubscriptions: boolean;
  loadingBusinessInvitations: boolean;
  businessSubscriptions: BusinessSubscription[];
  businessInvitations: BusinessInvitation[];
  onModalClose: () => void;
  addUsers: (emails: string, subscriptionId: string, role: UserBusinessRole) => Promise<LensSDKException | null>;
}

/**
 * The shape of the option data in the business subscription selector
 */
export interface SubscriptionOption {
  id: BusinessSubscription["id"];
  label: string;
}

/**
 * Get the shape of the option data to be used in the business subscription selector
 */
const getSubscriptionOption = (businessSubscription: BusinessSubscription): SubscriptionOption | undefined => {
  if (!businessSubscription) {
    return undefined;
  }

  return {
    id: businessSubscription.id,
    label: getPlanName(businessSubscription.planCode),
  };
};

const cancelButtonLabel = "Cancel";
const title = "Add Users";
const selectSubscriptionLabel = "Choose Lens Subscription";
const addNewSubscriptionLabel = "Add new Subscription";

const emailRequiredMessage = "Required";
const emailInvalidMessage = "Invalid email address(es)";

const addButtonLabel = "Add";

const AddUsersModal = ({
  open,
  adding,
  errorAdding,
  loadingBusinessSubscriptions,
  loadingBusinessInvitations,
  businessSubscriptions,
  businessInvitations,
  onModalClose,
  addUsers,
}: Props) => {
  const { track, trackButtonClicked } = useAnalytics();
  const history = useHistory();

  const [selectedBusinessSubscription, setSelectedBusinessSubscription] = useState<BusinessSubscription>();
  const seatCount = useMemo(
    () => getSubscriptionSeatCount(businessInvitations, selectedBusinessSubscription),
    [businessInvitations, selectedBusinessSubscription],
  );

  const [emails, setEmails] = useState<string>("");
  const [emailsValid, setEmailsValid] = useState<boolean>(false);
  const [emailsErrorMessage, setEmailsErrorMessage] = useState<string>("");

  /**
   * Return the errorMessage if the emails are invalid or undefined if valid
   */
  const emailsValidate = useCallback((emails: string, available: number) => {
    // emails is a empty string
    if (emails.length === 0) {
      return emailRequiredMessage;
    }

    const emailsArray = getUniqueEmailsArray(emails);
    const isAllEmailValid = emailsArray.every(validateEmail);

    if (!isAllEmailValid) {
      return emailInvalidMessage;
    }

    if (emailsArray.length > available === true) {
      return `Not enough seats available for ${emailsArray.length} users in this subscription`;
    }

    return undefined;
  }, []);

  useEffect(() => {
    const result = emailsValidate(emails, seatCount.available);

    setEmailsValid(result === undefined);
    setEmailsErrorMessage(result ?? "");
  }, [emails, emailsValidate, seatCount.available]);

  /**
   * Handle Subscription Selection
   */
  const [selected, setSelected] = useState<SubscriptionOption>();
  const defaultOption = useMemo(() => getSubscriptionOption(businessSubscriptions[0]), [businessSubscriptions]);
  const options = useMemo<SubscriptionOption[]>(
    () =>
      businessSubscriptions
        .map(getSubscriptionOption)
        // filter (unlikely) undefined returned from getOption
        .filter((option): option is SubscriptionOption => typeof option?.id === "string")
        // sort by label alphabetically
        .sort((a, b) => a.label.localeCompare(b.label)),
    [businessSubscriptions],
  );
  const chooseSubscription = useCallback(
    (option: SubscriptionOption | undefined) => {
      if (!option) {
        return;
      }
      track("Subscription Selected", { subscriptionId: option.id });
      setSelected(option);
    },
    [track, setSelected],
  );

  // set the default selected business subscription
  useEffect(() => {
    chooseSubscription(defaultOption);
  }, [defaultOption, chooseSubscription]);

  // "sync" the selected business subscription with the selected option
  useEffect(() => {
    setSelectedBusinessSubscription(businessSubscriptions.find(({ id }) => id === selected?.id));
  }, [selected, businessSubscriptions]);

  // Helper message
  const emailsMessage = useMemo(() => {
    if (!emailsValid || !selectedBusinessSubscription) {
      return;
    }
    const users = getUniqueEmailsArray(emails);

    return `Add ${users.length} user${users.length > 1 ? "s with seats" : " with a seat"} from the ${getPlanName(
      selectedBusinessSubscription.planCode,
    )} subscription`;
  }, [emails, emailsValid, selectedBusinessSubscription]);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setEmails("");
    setEmailsValid(false);
    setEmailsErrorMessage("");
    chooseSubscription(defaultOption);
  }, [chooseSubscription, defaultOption]);

  /**
   * Actions
   */
  const handleClickAdd = useCallback(async () => {
    trackButtonClicked(addButtonLabel);

    if (!selectedBusinessSubscription) {
      return;
    }

    const res = await addUsers(emails, selectedBusinessSubscription.id, "Member");

    if (!res) {
      reset();
    }
  }, [trackButtonClicked, addUsers, emails, selectedBusinessSubscription, reset]);

  const handleNewSubscriptions = () => {
    trackButtonClicked(addNewSubscriptionLabel);
    history.push("/subscribe/pro-yearly/configure");
  };

  return (
    <Modal
      title={title}
      size="md"
      contentProps={{
        className: styles.addUsersModalContent,
      }}
      onClose={() => {
        if (!adding) {
          reset();
          onModalClose();
        }
      }}
      isOpen={open}
      buttonBarProps={{
        type: "grid",
        gridSize: 3,
      }}
      footer={
        <>
          <button
            aria-label={cancelButtonLabel}
            type="button"
            onClick={() => {
              if (!adding) {
                trackButtonClicked(cancelButtonLabel);
                onModalClose();
                reset();
              }
            }}
            className="lds-button"
          >
            {cancelButtonLabel}
          </button>
          <div />
          <TrackedButton
            loading={adding || loadingBusinessSubscriptions || loadingBusinessInvitations}
            label={addButtonLabel}
            buttonType="submit"
            primary
            onClick={handleClickAdd}
            disabled={emailsValid === false || !selectedBusinessSubscription}
          />
        </>
      }
    >
      <form aria-label={title} autoComplete="off">
        {errorAdding ? (
          <Notification
            level="error"
            message={errorAdding?.message ?? "unknown"}
            type="flash"
            className={styles.errorNotification}
          />
        ) : null}
        <fieldset>
          <FormSelect
            name="subscription-selection"
            label={selectSubscriptionLabel}
            optionClassName={(state) => (state?.active === true ? styles.selectBusinessSubscriptionActive : "")}
            loading={loadingBusinessSubscriptions}
            value={selected}
            onChange={(value) => {
              chooseSubscription(value);
            }}
            options={options}
            renderContent={(value) =>
              value ? (
                <SubscriptionSelectContent
                  id={value.id}
                  businessSubscriptions={businessSubscriptions}
                  businessInvitations={businessInvitations}
                />
              ) : (
                ""
              )
            }
            renderAfterLastOption={() => (
              <button
                aria-label={addNewSubscriptionLabel}
                type="button"
                onClick={handleNewSubscriptions}
                className={styles.newSubscriptionButton}
              >
                {addNewSubscriptionLabel}
              </button>
            )}
          />
          <FormField
            id="emails"
            errorId="emailErrors"
            labelId="emailLabel"
            label="Add users to your Lens Business ID"
            errors={emailsErrorMessage ? [emailsErrorMessage] : []}
          >
            <textarea
              value={emails}
              onChange={({ target: { value } }) => {
                setEmails(value);
              }}
              autoComplete="off"
              name="user-emails"
              placeholder="Comma separated list of Email addresses"
              className={styles.emailTextarea}
              spellCheck="false"
            />
          </FormField>
          {emailsMessage ? (
            <div className={styles.emailsMessage}>
              <span>{emailsMessage}</span>
            </div>
          ) : null}
        </fieldset>
      </form>
    </Modal>
  );
};

export default AddUsersModal;
